import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import app from '../../main' // import the instance
import checkResponse from '@/utils/checkResponse'
import parserFilters from '@/utils/parserFilters'
import parserSlug from '@/utils/parserSlug'
import allPermissions from '@/utils/permissions'
import customersPermissions from '@/utils/permissionsCustomers'
import notificationNames from '@/utils/notificationNames'
Vue.use(Vuex)

const state = {
  users: [],
  employees: [],
  notifications: [],
  user: {
    permissions: []
  },
  paginationUsers: {
    currentPage: 1,
    perPage: 5,
    totalUsers: 0,
    totalPages: 0
  },
  paginationEmployees: {
    currentPage: 1,
    perPage: 5,
    totalUsers: 0,
    totalPages: 0
  },
  usersCatalog: {},
  filterParams: '',
  has_all_notifications: false,
  isModalVisible: false
}

const mutations = {
  SET_USERS (state, payload) {
    state.users = payload.data

    state.paginationUsers = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalUsers: payload.total,
      totalPages: payload.last_page
    }
  },
  SET_EMPLOYEES (state, payload) {
    state.employees = payload.data

    state.paginationEmployees = {
      currentPage: payload.current_page,
      perPage: payload.per_page,
      totalUsers: payload.total,
      totalPages: payload.last_page
    }
  },
  SET_NOTIFICATIONS_NAMES (state, payload) {
    state.notifications = payload.data.map(name => name.notification_name)
    state.has_all_notifications = payload.has_all
  },
  ADD_USER (state, payload) {
    state.users.unshift(payload)
  },
  UPDATE_USER (state, payload) {
    const idx = state.users.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.set(state.users, idx, payload)
    }
  },
  UPDATE_EMPLOYEES (state, payload) {
    const idx = state.employees.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.set(state.employees, idx, payload)
    }
  },
  DELETE_USER (state, payload) {
    const idx = state.users.findIndex(u => u.id === payload.id)

    if (idx >= 0) {
      Vue.delete(state.users, idx)
    }
  },
  DELETE_USERS (state, payload) {
    for (let id of payload.ids) {
      const idx = state.users.findIndex(u => u.id === id)

      if (idx >= 0) {
        Vue.delete(state.users, idx)
      }
    }
  },
  ADD_PERMISSIONS (state, payload) {
    const idx = state.user.permissions.findIndex(u => u === payload)
    if (idx === -1) {
      state.user.permissions.unshift(payload)
    }
  },
  ADD_NOTIFICATIONS (state, payload) {
    const idx = state.notifications.findIndex(u => u === payload)
    if (idx === -1) {
      state.notifications.unshift(payload)
    }
  },
  ADD_ALL_PERMISSIONS (state, payload) {
    if (payload) {
      state.user.permissions = allPermissions()
    } else {
      state.user.permissions = []
    }
  },
  ADD_ALL_NOTIFICATIONS (state, payload) {
    if (payload) {
      state.has_all_notifications = true
      state.notifications = notificationNames()
    } else {
      state.has_all_notifications = false
      state.notifications = []
    }
  },
  ADD_CUSTOMERS_PERMISSIONS (state, payload) {
    if (payload) {
      state.user.permissions = customersPermissions()
    } else {
      state.user.permissions = []
    }
  },
  DELETE_PERMISSIONS (state, payload) {
    const idx = state.user.permissions.findIndex(u => u === payload)
    if (idx >= 0) {
      Vue.delete(state.user.permissions, idx)
    }
  },
  DELETE_NOTIFICATIONS (state, payload) {
    state.has_all_notifications = false
    const idx = state.notifications.findIndex(u => u === payload)
    if (idx >= 0) {
      Vue.delete(state.notifications, idx)
    }
  },
  SET_MODAL_VISIBLE (state, payload) {
    state.isModalVisible = payload
  },
  SET_FILTER_PARAMS (state, payload) {
    state.filterParams = payload
  },
  SET_CATALOG (state, { object, payload }) {
    state.usersCatalog[object] = payload.data
  }
}

const actions = {
  async loadUsers ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`users?page=${payload.page}${state.filterParams}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_USERS', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadEmployee ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`users?page=${payload.page}${state.filterParams}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_EMPLOYEES', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async loadNotificationsNames ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.get(`notifications/${payload}/names`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_NOTIFICATIONS_NAMES', response.data)
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async addUser ({ commit }, payload) {
    const user = {
      name: payload.user.name,
      email: payload.user.email,
      password: payload.user.password,
      role: payload.user.role,
      permissions: payload.permissions
    }

    app.$Progress.start()

    try {
      const response = await axios.post('users', user)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('ADD_USER', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async updateNotifications ({ commit }, payload) {
    const notifications = {
      notifications: payload.notifications,
      user_id: payload.id
    }

    app.$Progress.start()

    try {
      const response = await axios.post(`notifications`, notifications)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.added_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async editUser ({ commit }, payload) {
    const user = {
      name: payload.name,
      email: payload.email,
      password: payload.password
    }

    app.$Progress.start()

    try {
      const response = await axios.put(`users/${payload.id}`, user)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_USER', response.data)
        commit('UPDATE_EMPLOYEES', response.data)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.updated_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async addPermissions ({ commit }, payload) {
    commit('ADD_PERMISSIONS', payload)
  },
  async deletePermissions ({ commit }, payload) {
    commit('DELETE_PERMISSIONS', payload)
  },
  async addNotifications ({ commit }, payload) {
    commit('ADD_NOTIFICATIONS', payload)
  },
  async deleteNotifications ({ commit }, payload) {
    commit('DELETE_NOTIFICATIONS', payload)
  },
  async deleteUser ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.delete(`users/${payload.id}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_USER', payload)
        commit('SET_MODAL_VISIBLE', false)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async deleteUsers ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.delete(`users/delete`, { params: { ids: payload.ids } })
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('DELETE_USERS', payload)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  async actionUser ({ commit }, payload) {
    app.$Progress.start()
    try {
      const response = await axios.put(`users/${payload.id}/${payload.action}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('UPDATE_USER', response.data)
        commit('UPDATE_EMPLOYEES', response.data)
        commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: `users.${payload.action}`, icon: 'checkmark-circle-outline' }, { root: true })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  printReport ({ commit }, payload) {
    app.$Progress.start()
    try {
      window.open(axios.defaults.baseURL + '/users/export?' + state.filterParams)
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  applyFilter ({ commit }, payload) {
    const filter = parserFilters(payload)
    commit('SET_FILTER_PARAMS', filter)
  },
  clearFilter ({ commit }, payload) {
    const filter = ''
    commit('SET_FILTER_PARAMS', filter)
  },
  async loadCatalog ({ commit }, payload) {
    let url = parserSlug(payload.url)
    app.$Progress.start()
    try {
      const response = await axios.get(`${url}${payload.filter}`)
      const checkErrors = checkResponse(response)

      if (checkErrors) {
        app.$Progress.fail()
        commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: checkErrors.message }, { root: true })
      } else {
        commit('SET_CATALOG', { object: payload.url, payload: response.data })
      }
    } catch (e) {
      app.$Progress.fail()
      commit('SET_ALERT_MESSAGE', { color: 'danger', title: 'errors.connection_error', text: 'errors.generic_error' }, { root: true })
    } finally {
      app.$Progress.finish()
    }
  },
  setModalVisible ({ commit }, payload) {
    commit('SET_MODAL_VISIBLE', payload)
  },
  addAllPermissions ({ commit }, payload) {
    commit('ADD_ALL_PERMISSIONS', payload)
  },
  addAllNotificationsNames ({ commit }, payload) {
    commit('ADD_ALL_NOTIFICATIONS', payload)
  },
  addCustomersPermissions ({ commit }, payload) {
    commit('ADD_CUSTOMERS_PERMISSIONS', payload)
  },
  alertMessage ({ commit }, payload) {
    commit('SET_ACCEPT_ALERT', { color: 'success', title: 'strings.users', text: 'front.deleted_successfully', icon: 'checkmark-circle-outline' }, { root: true })
  }
}

const getters = {
  users: state => state.users,
  employees: state => state.employees,
  user: state => state.user,
  notifications: state => state.notifications,
  usersCatalog: state => state.usersCatalog,
  paginationUsers: state => state.paginationUsers,
  paginationEmployees: state => state.paginationEmployees,
  has_all_notifications: state => state.has_all_notifications,
  isModalVisible: state => state.isModalVisible,
  filterParams: state => state.filterParams
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
